import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';


const BOIReportInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="FinCEN BOI Reporting Instructions: A Comprehensive Guide"
          description="Learn how to comply with FinCEN’s BOI (Beneficial Ownership Information) reporting requirements. Follow our detailed instructions for easy filing and avoid penalties."
          keywords=""
          canonical="resources/fincen-boir/boi-reporting-instructions/"
          ogType="article"
          jsonLdPricing="39.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Alert
          severity="warning"
          icon={<WarningOutlinedIcon sx={{ fontSize: 44 }} />}
          sx={{ borderRadius: 3, mt: 3 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              component="div"
              variant="body1"
              sx={{
                textAlign: { xs: 'left', sm: 'left' },
                fontSize: { xs: 13, sm: 14, md: 15 },
                //  color: '#ccc',
                lineHeight: 1.3,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700, mb: 0 }}>
                BOI Compliance Update:
              </AlertTitle>
              No reporting is required for U.S. businesses and citizens. Only
              Foreign Reporting Companies must comply.{' '}
              <Link
                href="https://blog.taxzerone.com/posts/boi-report-fincen-revises-reporting-rules-now-focused-on-foreign-entities/"
                target="_blank"
                sx={{ color: '#0202ff' }}
              >
                See Details
              </Link>
              .
            </Typography>
          </Stack>
        </Alert>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          General Instructions for{' '}
          <span className="highlight-container">
            <span className="highlight"> BOI Reporting</span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Instruction Essential for Reporting Beneficial Ownership Information
          of Your Organization
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirinstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
         <Container maxWidth="lg" sx={{ mb: 0 }}>
          {process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' && (
            <DynamicBreadcrumbs />
          )}
          <Grid container spacing={0} rowSpacing={0} m={0} sx={{flexDirection:'row-reverse'}}>
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Beneficial Ownership Information (BOI)?
                </Typography>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      <Link href="/efile-boi-report/" sx={{ color: '#0202ff' }}>
                        {' '}
                        Beneficial Ownership Information
                      </Link>{' '}
                      (BOI) refers to the details of individuals who are
                      considered the beneficial owners of a company or entity.
                      Beneficial owners are the people who have substantial
                      control or ownership interest in the organization. As the
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Corporate Transparency Act
                      </span>{' '}
                      requires, the details of the beneficial owners and company
                      applicants must be reported to{' '}
                      <span style={{ fontWeight: 600 }}>FinCEN</span> by
                      companies registered with the Secretary of State or a
                      similar office.
                    </Typography>
                  </Box>
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Needs to Report? Understanding Reporting Companies
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  A company required to submit Beneficial Ownership Information
                  (BOI) to FinCEN is called a &quot;Reporting Company.&quot;
                  There are two types of Reporting Companies:
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> Domestic Reporting Companies:</b> These include
                    corporations, limited liability companies (LLCs), and other
                    entities that are formed and registered to conduct business
                    in the U.S. through the process of filing documents with the
                    Secretary of State or a comparable office They are subject
                    to BOI reporting requirements.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Foreign Reporting Companies:</b> These are corporations,
                    LLCs, or other entities formed under foreign laws but
                    registered to operate in the U.S. by filing documents with
                    the Secretary of State or equivalent offices. They, too, are
                    required to report BOI.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3, fontWeight: 600 }}>
                  For details on when to submit your BOI report, visit our{' '}
                  <Link
                    href="/resources/fincen-boir/boi-reporting-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    BOI filing due dates
                  </Link>{' '}
                  page
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Are the Beneficial Owners of Your Company?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Beneficial owners</b> are individuals who either possess a
                  significant ownership interest in a reporting company or
                  exercise substantial control over its operations. Below is
                  detailed information on what is ownership interest and
                  substantial control.
                </Typography>

                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                              width: 300,
                            }}
                          >
                            Ownership interest
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', fontWeight: 600, width: 300 }}
                          >
                            Substantial control
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            An individual who owns or controls 25% or more of
                            the ownership interest in a reporting company must
                            be reported in the Beneficial Ownership Information
                            (BOI) filed with FinCEN. Ownership interest can take
                            many forms, including:
                            <ul style={{ marginBottom: 20 }}>
                              <li style={{ marginBottom: 12 }}>
                                <b> Equity, stock, or voting rights</b> in the
                                reporting company.
                              </li>
                              <li style={{ marginBottom: 12 }}>
                                <b> Capital gains or profit interests</b>{' '}
                                received from the reporting company.
                              </li>
                              <li style={{ marginBottom: 12 }}>
                                The <b>right to purchase, sell, or subscribe</b>{' '}
                                to equity, stock, voting rights, or capital
                                gains—even if the right is tied to a debt
                                instrument.
                              </li>
                              <li style={{ marginBottom: 12 }}>
                                <b> Options or privileges</b> to buy or sell the
                                company’s equity, stock, voting rights, or
                                profit interests, as well as convertible
                                instruments.
                              </li>
                              <li style={{ marginBottom: 12 }}>
                                Any <b>contract, relationship, or mechanism</b>{' '}
                                that establishes ownership of the reporting
                                company.
                              </li>
                            </ul>
                          </TableCell>
                          <TableCell>
                            An individual who has the power to influence or
                            control a reporting company&apos;s decision-making,
                            whether directly or indirectly, must be reported as
                            a Beneficial Owner. This control can include
                            decisions related to the company&apos;s structure,
                            finances, or operations. Key indicators of
                            substantial control include:
                            <ul style={{ marginBottom: 20 }}>
                              <li style={{ marginBottom: 12 }}>
                                Holding a senior officer position, such as
                                <b>
                                  {' '}
                                  President, CEO, Director, CFO, General
                                  Counsel, COO,
                                </b>{' '}
                                or any role with decision-making authority.
                              </li>
                              <li style={{ marginBottom: 12 }}>
                                Having the power to{' '}
                                <b>appoint or remove senior officers</b> of the
                                company.
                              </li>
                              <li style={{ marginBottom: 12 }}>
                                Exercising significant influence over the
                                <b> entity&apos;s decision-making</b>.
                              </li>
                              <li style={{ marginBottom: 12 }}>
                                Using <b>indirect methods</b> to control the
                                company which is possible in a flexible
                                corporate structure is also considered
                                substantial control.
                              </li>
                            </ul>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Different Types of BOI Reporting
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 5,
                        textAlign: 'center',
                        minHeight: '100%',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '20px !important',
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Initial Report
                      </Typography>
                      <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        Any reporting company submitting its Beneficial
                        Ownership Information (BOI) to FinCEN for the first time
                        should choose the &apos;Initial Report&apos; option
                        during the submission process.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 5,
                        textAlign: 'center',
                        minHeight: '100%',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '20px !important',
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Correct Prior Report
                      </Typography>
                      <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        If a reporting company identifies an error in its
                        previously submitted BOI report to FinCEN, the Correct
                        Prior Report&quot; option should be selected to amend
                        and correct the information. Ensuring accurate data is
                        provided to FinCEN is essential to avoid potential
                        penalties.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 5,
                        textAlign: 'center',
                        minHeight: '100%',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '20px !important',
                          fontWeight: 600,
                          mb: 1,
                          textAlign: 'center',
                        }}
                      >
                        Update Prior Report
                      </Typography>
                      <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        A reporting company that has already submitted its BOI
                        report but needs to update information—such as changes
                        in Beneficial Owners—should select the &quot;Update
                        Prior Report&quot; option. This may occur during events
                        like leadership changes, the sale of the company, or
                        changes in ownership.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 5,
                        textAlign: 'center',
                        minHeight: '100%',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '20px !important',
                          fontWeight: 600,
                          mb: 1,
                          textAlign: 'center',
                        }}
                      >
                        Newly Exempt Entity
                      </Typography>
                      <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        A reporting company that has previously submitted a BOI
                        report but has since gained exempt status should choose
                        the &quot;Newly Exempt Entity&quot; option when updating
                        its BOI information with FinCEN.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                {/* <Box component={'div'} sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead></TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell colSpan={2} sx={{ textAlign: 'center' }}>
                            <b style={{ fontSize: '20px !important' }}>
                              Initial Report
                            </b>
                            <br />
                            Any reporting company submitting its Beneficial
                            Ownership Information (BOI) to FinCEN for the first
                            time should choose the &apos;Initial Report&apos;
                            option during the submission process.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              fontWeight: 600,
                              alignItems: 'center',
                              width: 300,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Correct Prior Report
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              fontWeight: 600,
                              alignItems: 'center',
                              width: 300,
                            }}
                          >
                            Update Prior Report
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            If a reporting company identifies an <b>error</b> in
                            its previously submitted BOI report to FinCEN, the
                            &quot;Correct Prior Report&quot; option should be
                            selected to amend and correct the information.
                            Ensuring accurate data is provided to FinCEN is
                            essential to avoid potential penalties.
                          </TableCell>
                          <TableCell>
                            A reporting company that has already submitted its
                            BOI report but needs to update information—such as{' '}
                            <b>changes in Beneficial Owners</b>—should select
                            the &quot;Update Prior Report&quot; option. This may
                            occur during events like leadership changes, the
                            sale of the company, or changes in ownership.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell colSpan={2} sx={{ textAlign: 'center' }}>
                            <b style={{ fontSize: '20px !important' }}>
                              Newly Exempt Entity
                            </b>
                            <br />A reporting company that has previously
                            submitted a BOI report but has since gained exempt
                            status should choose the &quot;Newly Exempt
                            Entity&quot; option when updating its BOI
                            information with FinCEN.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Understanding BOI reporting: The three key parts
                </Typography>
                <picture>
                  <source srcSet="/assets/images/boir-three-part-img.webp" />
                  <source srcSet="/assets/images/boir-three-part-img.png" />
                  <img
                    src="/assets/images/boir-three-part-img.png"
                    loading="lazy"
                    alt="Understanding BOI reporting: The three key parts"
                    width="100%"
                    height={430}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1">
                  BOI (Beneficial Ownership Information) reporting is divided
                  into three essential parts:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> Reporting Company:</b> This collects the required
                    information about the entity that must disclose beneficial
                    ownership details.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Company Applicant:</b> The information about the
                    individual responsible for filing the formation or
                    registration documents of the company.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Beneficial Owner:</b> Information about the persons who
                    directly or indirectly own or control at least 25% of the
                    company or have significant control over its operations.
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Information Required from the{' '}
                  <span className="highlight-container">
                    <span className="highlight">Reporting Company</span>{' '}
                  </span>{' '}
                  in the BOI Report
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When it comes to BOI reporting, the Reporting Company must
                  provide FinCEN with all relevant details, including:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> Type of filing:</b> Whether it&apos;s an Initial Report,
                    a correction to a prior report, an update, or information
                    for a newly exempt entity.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Legal and alternate names:</b> The company’s official
                    legal name and any alternate names it may operate under.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Form of identification:</b> The specific identification
                    details required for the entity.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Jurisdiction of formation/registration:</b> The state or
                    jurisdiction where the company was formed or first
                    registered.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Current U.S. address:</b> The company’s most up-to-date
                    physical address in the United States.
                  </li>
                </ul>
                <Alert severity="warning" icon={false} sx={{ mt: 4, mb: 4 }}>
                  <b>Note:</b> If the entity is registered under a foreign
                  country law then select the Country name in the Jurisdiction
                  of formation or first registration
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Key details every{' '}
                  <span className="highlight-container">
                    <span className="highlight">Company Applicant</span>
                  </span>{' '}
                  should provide in the BOI filing
                </Typography>
                <Typography variant="body1">
                  The Company Applicant is the individual responsible for
                  submitting the entity&apos;s registration. Up to two Company
                  Applicants can be reported for each entity. When filing BOI
                  information, the following details about the Company Applicant
                  must be provided:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> FinCEN ID (If applicable):</b> A unique identifier
                    issued by FinCEN, if the applicant has one.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Legal name and Date of birth:</b> The full legal name
                    and birthdate of the applicant.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Current address:</b> The applicant’s current business or
                    residential address.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Form of identification and issuing jurisdiction:</b> The
                    type of identification provided, along with the jurisdiction
                    that issued it.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Upload identification document:</b> A digital copy of
                    the identification document must be uploaded for
                    verification.
                  </li>
                </ul>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 0 }}
                >
                  <b>Important:</b> Entities registered before January 1, 2024,
                  are not required to provide Company Applicant information in
                  the BOI report. Similarly, foreign entities that registered to
                  do business in the U.S. before January 1, 2024, are not
                  required to provide Company Applicant information in the BOI
                  report.
                </Alert>
                <Alert severity="warning" icon={false} sx={{ mt: 2, mb: 3 }}>
                  <b>Note:</b> If the Company Applicant is a foreign national,
                  be sure to include their foreign address and specify the
                  issuing jurisdiction of their ID card
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Details you must submit about{' '}
                  <span className="highlight-container">
                    <span className="highlight">Beneficial Owners</span>
                  </span>
                </Typography>
                <Typography variant="body1">
                  In the BOI reporting process, the Beneficial Owner&apos;s
                  details must be provided for the reporting company. The
                  required information includes:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> FinCEN ID (If applicable):</b> A unique identifier
                    issued by FinCEN, if the beneficial owner has one.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Legal name and Date of birth:</b> The full legal name
                    and birthdate of the beneficial owner.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Residential address:</b> The residential address where
                    the beneficial owner currently resides.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Form of identification:</b> The type of identification
                    provided, along with the issuing jurisdiction.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Upload identification document:</b> A digital copy of the
                    identification document must be uploaded for verification.
                  </li>
                </ul>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 0 }}
                >
                  <b>Important:</b> If the beneficial owner is a minor, the
                  &quot;Parent/Guardian Information Instead of Minor Child&quot;
                  checkbox should be selected, and the details of the parent or
                  guardian must be provided as the beneficial owner.
                </Alert>
                <Alert severity="warning" icon={false} sx={{ mt: 2, mb: 3 }}>
                  <b>Note:</b> If the Beneficial Owner is a foreign national,
                  ensure to provide their foreign address and specify the
                  issuing jurisdiction of their ID card.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates in BOI reporting
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 100,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            S.No
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Condition
                          </TableCell>
                          <TableCell sx={{ width: 300, fontWeight: 600 }}>
                            Due Dates
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Entity registered before January 1, 2024
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 1, 2025
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Entity registered on or after January 1, 2024, and
                            before January 1, 2025
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            90 days from the date of registration
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            3
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Entity registered on or after January 1, 2025
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            30 days from the date of registration
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            4
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Any updates to the entity, such as changes in
                            beneficial owners, etc.
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            30 days after the change happened
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            5
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Any corrections to the BOI report already submitted
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            30 days after identifying the error
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{ borderRadius: 3, textAlign: 'left', mt: 2 }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontSize: '18xp !important', mb: 1 }}
                    >
                      <b>Important Update: BOI Reporting is Required Again!</b>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.3 }}>
                      Beneficial Ownership Information (BOI) reporting
                      requirements are back in effect following a recent court
                      decision. Most companies must file by the new deadline of{' '}
                      <b>March 21, 2025</b>.
                    </Typography>
                    <Typography variant="body2">
                      <b> Avoid penalties</b>—
                      <Link href="/efile-boi-report/" sx={{ color: '#0202ff' }}>
                        file your BOI report
                      </Link>{' '}
                      with ease today!
                    </Typography>
                  </Alert>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who doesn’t count as a beneficial owner?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Under five categories an Individual can be determined as he is
                  not the Beneficial Owner of the company.
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead />
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            colSpan={2}
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <b style={{ fontSize: '20px !important' }}>
                              Nominees, Intermediaries, Custodians, or Agents
                            </b>
                            <br />
                            Individuals who act on behalf of an actual
                            Beneficial Owner do not need to be reported.
                            However, the actual Beneficial Owner must still be
                            listed in the BOI submission.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',

                              alignItems: 'center',
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <b style={{ fontSize: '20px !important' }}>
                              {' '}
                              Minor Child
                            </b>
                            <br />
                            If the Beneficial Owner is a minor, their
                            information does not need to be reported to FinCEN.
                            Instead, the parent or legal guardian&apos;s details
                            must be provided in the BOI report.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                              alignItems: 'center',
                              width: 300,
                            }}
                          >
                            <b style={{ fontSize: '20px !important' }}>
                              {' '}
                              Inheritors{' '}
                            </b>
                            <br />
                            Individuals whose only interest in the company is
                            through the right of inheritance, such as those set
                            to inherit the company in the future, do not need to
                            be reported in the BOI.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <b style={{ fontSize: '20px !important' }}>
                              {' '}
                              Employees:{' '}
                            </b>
                            <br />
                            Employees who do not hold senior positions, such as
                            President, CFO, General Counsel, CEO, or COO, and
                            whose control or benefits are solely tied to their
                            employment can also be excluded from the
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <b style={{ fontSize: '20px !important' }}>
                              {' '}
                              Creditors:{' '}
                            </b>
                            <br />
                            Individuals who lend a specific sum to the company,
                            and whose only right is to receive repayment, are
                            not considered Beneficial Owners and are exempt from
                            being reported.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  BOI Reporting: Entities that are exempt from filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  There are 23 categories of entities that are exempt from
                  Beneficial Ownership Information reporting requirements.
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ width: 100, fontWeight: 600 }}>
                            Exemption No.
                          </TableCell>
                          <TableCell sx={{ width: 300, fontWeight: 600 }}>
                            Types of entities
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ textAlign: 'left' }}>1</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Securities reporting issuer
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>2</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Governmental authority
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>3</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>Bank</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>4</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Credit union
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>5</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Depository institution holding company
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>6</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Money services business
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>7</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Broker or dealer in securities
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>8</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Securities exchange or clearing agency
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>9</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Other Exchange Act registered entity
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>10</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            An investment company or investment adviser
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>11</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Venture capital fund adviser
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>12</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Insurance company
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>13</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            State-licensed insurance producer
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>14</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Commodity Exchange Act registered entity
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>15</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Accounting firm
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>16</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Public utility
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>17</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Financial market utility
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>18</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Pooled investment vehicle
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>19</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Tax-exempt entity
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>20</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Entity assisting a tax-exempt entity
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>21</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Large operating company
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>22</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Subsidiary of certain exempt entities
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'left' }}>23</TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Inactive entity
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mt: 3, mb: 2 }}>
                  <b>Note:</b> For more details check FinCEN&apos;s.{' '}
                  <Link
                    href="https://www.fincen.gov/boi/small-entity-compliance-guide"
                    sx={{ color: '#0202ff' }}
                    target="_blank"
                  >
                    Small Entity Compliance Guide
                  </Link>{' '}
                  for checklists detailing each of the 23 exemptions, which may
                  assist in determining if a company qualifies for an exemption
                  (refer to Chapter 1.2, &quot;Is my company exempt from the
                  reporting requirements?&quot;)
                </Alert>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        Financial Crimes Enforcement Network (FinCEN). <br />
                        For more details, please refer to the FinCEN Beneficial
                        Ownership Information Reporting website:{' '}
                        <Link
                          href="https://fincen.gov/boi-faqs"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          boiefiling.fincen.gov
                        </Link>
                        . <br />
                        <br />
                        You can also access the FinCEN Small Entity Compliance
                        Guide:{' '}
                        <Link
                          href="https://www.fincen.gov/boi/small-entity-compliance-guide"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          Small Entity Compliance Guide
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File your BOI Report with TaxZerone
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Submit your BOI Report to FinCEN at just <b>$39.99 Per</b> EIN
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirinstructions`}
              >
                E-file BOI Report Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default BOIReportInstructions;
